<template>
  <v-app>
    <!-- Hero Section -->
    <v-container fluid class="hero-section primary" style="background: linear-gradient(135deg, #8D4E3C 0%, #6D4C41 100%);">
      <v-container>
        <v-row align="center" justify="center" class="py-8">
          <v-col cols="12" md="8" class="text-center">
            <v-icon size="64" color="white" class="mb-4">mdi-history</v-icon>
            <h1 class="display-1 white--text font-weight-bold mb-3">
              Historial de Turnos
            </h1>
            <p class="title white--text mb-4">
              Gestione sus citas médicas y consulte su historial
            </p>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- Main Content -->
    <v-container class="py-8">
      <!-- Info Alert -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-alert
            type="info"
            border="left"
            colored-border
            class="mb-4 rounded-lg"
            elevation="2"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-3" color="info">mdi-information</v-icon>
              <div>
                <p class="mb-0">
                  Puede consultar al número <strong>291-407-1621</strong> si el profesional con el que se atenderá trabaja con su obra social.
                </p>
              </div>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Appointments Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="2" class="rounded-lg">
            <v-card-title class="primary white--text">
              <v-icon left color="white">mdi-calendar-check</v-icon>
              Mis Turnos
              <v-spacer></v-spacer>
              <v-btn icon color="white" @click="getAppointments">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>
            
            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="appointments"
                :loading="loadding"
                class="modern-table"
                :no-data-text="'No se encontraron turnos registrados'"
                loading-text="Cargando historial de turnos..."
                :items-per-page="10"
              >
                <!-- Custom Date Column -->
                <template v-slot:item.start="{ item }">
                  <div class="d-flex align-center font-weight-medium">
                    <v-icon small class="mr-2" color="primary">mdi-calendar</v-icon>
                    {{ formatDate(item.start) }}
                  </div>
                </template>

                <!-- Custom Doctor Column -->
                <template v-slot:item.doctor="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" color="grey lighten-3" class="mr-2">
                       <v-icon small>mdi-doctor</v-icon>
                    </v-avatar>
                    <div>
                        <div class="font-weight-medium">Dr/a. {{ item.lastname }} {{ item.name }}</div>
                        <div class="caption grey--text">{{ item.especialidad || item.specialty || '' }}</div>
                    </div>
                  </div>
                </template>

                 <!-- Custom Status Column -->
                <template v-slot:item.state="{ item }">
                  <v-chip
                    :color="getStatusColor(item.state)"
                    dark
                    small
                    class="font-weight-medium"
                  >
                    <v-icon left small>{{ getStatusIcon(item.state) }}</v-icon>
                    {{ getStatusText(item.state) }}
                  </v-chip>
                </template>

                 <!-- Actions Column -->
                <template v-slot:item.actions="{ item }">
                    <v-btn
                        v-if="canCancel(item.state)"
                        color="error"
                        small
                        text
                        @click="openStateDialog(item)"
                    >
                        <v-icon left small>mdi-cancel</v-icon>
                        Cancelar
                    </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

      <!-- Cancellation Dialog -->
     <v-dialog v-model="stateDialog" max-width="500px">
      <v-card class="rounded-lg">
        <v-card-title class="headline error white--text">
            <v-icon left color="white">mdi-alert-circle</v-icon>
            Cancelar Turno
        </v-card-title>
        
        <v-card-text class="pt-4">
          <p class="subtitle-1">
            ¿Por qué desea cancelar este turno con <strong>{{ selectedAppointment ? selectedAppointment.doctor : '' }}</strong>?
          </p>
          
          <v-radio-group v-model="selectedState" column>
            <v-radio
              v-for="option in cancellationOptions"
              :key="option.value"
              :label="option.text"
              :value="option.value"
              color="error"
            ></v-radio>
          </v-radio-group>
          
          <v-alert type="warning" dense text class="mt-2" v-if="selectedState === 'user_cancelled'">
            Esta acción liberará el turno para que otro paciente pueda reservarlo.
          </v-alert>
          <v-alert type="info" dense text class="mt-2" v-if="selectedState === 'center_cancelled'">
            El turno quedará bloqueado y no podrá ser reservado por otro paciente.
          </v-alert>
        </v-card-text>
        
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="stateDialog = false">
            No, Mantener Turno
          </v-btn>
          <v-btn 
            color="error" 
            @click="saveStateChange"
            :loading="loadding"
            elevation="2"
          >
            Confirmar Cancelación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { getTurnosPaciente, getTurnosPacientePorDni, cambiarEstadoTurno } from '@/services/appointmentsApi';

export default {
  name: "CitasPaciente",
  props: {
      dni: {
          type: String,
          default: null
      }
  },
  data() {
    return {
      appointments: [],
      loadding: false,
      totalRows: 0,
      headers: [
        { text: 'Fecha y Hora', value: 'start', sortable: true },
        { text: 'Profesional', value: 'doctor', sortable: true },
        { text: 'Estado', value: 'state', sortable: true },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end' },
      ],
      
      // Dialog state
      stateDialog: false,
      selectedAppointment: null,
      selectedState: 'user_cancelled',
      cancellationOptions: [
          { text: 'Cancelado por el paciente (Libera turno)', value: 'user_cancelled' },
          // Solo mostrar opción de paciente, las otras son para admins/secretarias
          // { text: 'Cancelado por el centro (No libera turno)', value: 'center_cancelled' },
          // { text: 'No se presentó (Ausente)', value: 'missed' }
      ]
    }
  },
  
  created() {
    this.getAppointments();
  },
  
    watch: {
    dni: {
      handler(newVal) {
        if (newVal) {
          this.getAppointments();
        }
      },
      immediate: true
    }
  },

  methods: {
    canCancel(state) {
        return state === 'confirmed' || state === 'checked_in';
    },

    formatDate(dateStr) {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('es-AR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    getStatusColor(state) {
        const colors = {
            'confirmed': 'success',
            'checked_in': 'info',
            'done': 'grey',
            'user_cancelled': 'error',
            'center_cancelled': 'error',
            'missed': 'warning'
        };
        return colors[state] || 'grey';
    },

    getStatusIcon(state) {
        const icons = {
            'confirmed': 'mdi-check-circle',
            'checked_in': 'mdi-account-clock',
            'done': 'mdi-check-all',
            'user_cancelled': 'mdi-account-cancel',
            'center_cancelled': 'mdi-hospital-building',
            'missed': 'mdi-account-alert'
        };
        return icons[state] || 'mdi-help-circle';
    },

    getStatusText(state) {
        const texts = {
            'confirmed': 'Confirmado',
            'checked_in': 'En Espera',
            'done': 'Finalizado',
            'user_cancelled': 'Cancelado (P)',
            'center_cancelled': 'Cancelado (C)',
            'missed': 'Ausente',
            'free': 'Disponible'
        };
        return texts[state] || state;
    },

    openStateDialog(item) {
        this.selectedAppointment = item;
        this.selectedState = 'user_cancelled'; // Default for patients
        this.stateDialog = true;
    },

    async saveStateChange() {
      if (!this.selectedAppointment || !this.selectedState) return;

      this.loadding = true;
      try {
        const result = await cambiarEstadoTurno(this.selectedAppointment.id, this.selectedState);

        if (result.success) {
          await this.$alert({
            title: 'Turno Cancelado',
            message: 'Su turno ha sido cancelado exitosamente.',
            color: 'success'
          });
          this.stateDialog = false;
          this.getAppointments(); // Refresh list
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('Error actualizando estado:', error);
        await this.$alert({
          title: 'Error',
          message: 'No se pudo cancelar el turno. Intente nuevamente.',
          color: 'error'
        });
      } finally {
          this.loadding = false;
      }
    },
    
    async getAppointments() {
      this.loadding = true;
      
      // LOGIC FIX: Check DNI from Store credentials as fallback
      const storeDni = this.$store.state.credentials ? this.$store.state.credentials.dni : null;
      const targetDni = this.dni || storeDni;
      
      // Obtener email del store (el nuevo API usa email, no DNI)
      const email = this.$store.state.credentials.fedacct || '';
      
      // CASO 1: Tenemos un DNI (Ya sea por Prop o por Store)
      if (targetDni) {
          try {
             const citas = await getTurnosPacientePorDni(targetDni);
             this.appointments = citas;
             this.totalRows = citas.length;
             this.loadding = false;
             return; // Éxito
          } catch (error) {
              console.error('❌ Error obteniendo turnos por DNI:', error);
              // No retornamos, intentamos fallback a email si falla
          }
      }

      // CASO 2: Fallback a Email si no hay DNI o falló la búsqueda por DNI
      if (email) {
        try {
            const citas = await getTurnosPaciente(email);
            this.appointments = citas;
            this.totalRows = citas.length;
        } catch (error) {
            console.error('❌ Error obteniendo turnos por Email:', error);
            this.appointments = [];
        }
      } else {
          this.appointments = [];
      }
      
      this.loadding = false;
    }
  }  
}
</script>

<style scoped>
/* Hero Section */
.hero-section {
  color: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

/* Card Styles */
.rounded-lg {
  border-radius: 12px !important;
}

.v-card-title {
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* Table Styles */
.modern-table {
  border-radius: 0 0 12px 12px;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 0 12px 12px;
}

/* Chip Styles */
.v-chip {
  font-weight: 600;
}

/* Alert Styles */
.v-alert {
  border-radius: 8px !important;
}
</style>
